import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
/* eslint-disable no-restricted-imports --
   styled() 베이스. 사용자 액션 버튼(닫기·이전/다음·페이지 dot)은 렌더 시 `as={SccPressable}` 로
   교체되어 정상 로깅된다. 유일한 raw Touchable 인 ContentWrapper 는 오버레이 클릭이 콘텐츠까지
   전파되는 걸 막는 용도(stopPropagation)라 사용자 액션이 아니고 계측 대상도 아니다.
   (터치 컴포넌트는 Scc* 사용 원칙: .eslintrc.js no-restricted-imports) */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { View, Modal, TouchableOpacity, Pressable, ScrollView, } from 'react-native';
import styled from 'styled-components/native';
import SccPressable from '@/components/SccPressable';
import SccRemoteImage from '@/components/SccRemoteImage';
import { color } from '@/constant/color';
import IcLeft from '@/assets/icon/ic_left.svg';
import IcRight from '@/assets/icon/ic_right.svg';
import { useResponsive } from '../context/ResponsiveContext';
export default function RegionDetailModal({ visible, allRegions, initialRegionId, onClose, }) {
    const { isDesktop } = useResponsive();
    const [currentIndex, setCurrentIndex] = useState(0);
    const scrollViewRef = useRef(null);
    const isScrollingProgrammatically = useRef(false);
    // 1. 모든 이미지를 flatmap + 메타데이터 유지 (모바일/데스크탑 분기)
    const allImages = useMemo(() => {
        return (allRegions !== null && allRegions !== void 0 ? allRegions : []).flatMap((region) => {
            var _a;
            // 모바일이고 mobileModalImageUrls가 있으면 사용, 없으면 modalImageUrls 사용
            const imageUrls = !isDesktop && ((_a = region.mobileModalImageUrls) === null || _a === void 0 ? void 0 : _a.length)
                ? region.mobileModalImageUrls
                : region.modalImageUrls || [];
            return imageUrls.map((url) => ({
                url,
                regionId: region.id,
                regionTitle: region.title || '',
            }));
        });
    }, [allRegions, isDesktop]);
    // 2. 초기 인덱스 계산 (클릭한 region의 첫 이미지)
    const initialIndex = useMemo(() => {
        if (!initialRegionId)
            return 0;
        const idx = allImages.findIndex((img) => img.regionId === initialRegionId);
        return idx >= 0 ? idx : 0;
    }, [allImages, initialRegionId]);
    // 3. 모달 열릴 때 초기 인덱스로 설정 + 스크롤 이동
    useEffect(() => {
        if (visible && initialIndex >= 0) {
            setCurrentIndex(initialIndex);
            // 약간의 딜레이 후 스크롤 (렌더링 완료 후)
            setTimeout(() => {
                if (scrollViewRef.current) {
                    const pageWidth = isDesktop ? 600 : window.innerWidth - 48;
                    scrollViewRef.current.scrollTo({ x: initialIndex * pageWidth, animated: false });
                }
            }, 50);
        }
    }, [visible, initialIndex, isDesktop]);
    const handleScroll = useCallback((event) => {
        // 프로그래매틱 스크롤 중에는 무시
        if (isScrollingProgrammatically.current)
            return;
        const offsetX = event.nativeEvent.contentOffset.x;
        const pageWidth = event.nativeEvent.layoutMeasurement.width;
        if (pageWidth > 0) {
            const index = Math.round(offsetX / pageWidth);
            setCurrentIndex(index);
        }
    }, []);
    const handleClose = useCallback(() => {
        setCurrentIndex(0);
        onClose();
    }, [onClose]);
    const handlePrev = useCallback(() => {
        if (currentIndex > 0) {
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            if (scrollViewRef.current) {
                isScrollingProgrammatically.current = true;
                const pageWidth = isDesktop ? 600 : window.innerWidth - 48;
                scrollViewRef.current.scrollTo({ x: newIndex * pageWidth, animated: true });
            }
        }
    }, [currentIndex, isDesktop]);
    const handleNext = useCallback(() => {
        if (currentIndex < allImages.length - 1) {
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            if (scrollViewRef.current) {
                isScrollingProgrammatically.current = true;
                const pageWidth = isDesktop ? 600 : window.innerWidth - 48;
                scrollViewRef.current.scrollTo({ x: newIndex * pageWidth, animated: true });
            }
        }
    }, [currentIndex, allImages.length, isDesktop]);
    const handleDotPress = useCallback((index) => {
        setCurrentIndex(index);
        if (scrollViewRef.current) {
            isScrollingProgrammatically.current = true;
            const pageWidth = isDesktop ? 600 : window.innerWidth - 48;
            scrollViewRef.current.scrollTo({ x: index * pageWidth, animated: true });
        }
    }, [isDesktop]);
    const handleMomentumScrollEnd = useCallback(() => {
        isScrollingProgrammatically.current = false;
    }, []);
    if (allImages.length === 0) {
        return null;
    }
    const hasMultipleImages = allImages.length > 1;
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < allImages.length - 1;
    return (_jsx(Modal, { visible: visible, transparent: true, animationType: "fade", onRequestClose: handleClose, children: _jsx(ModalOverlay, { as: SccPressable, onPress: handleClose, elementName: "bbucle-road-modal-close", logParams: { regionId: initialRegionId, isDesktop }, children: _jsx(ContentWrapper, { activeOpacity: 1, onPress: (e) => e.stopPropagation(), children: _jsxs(ModalContent, { isDesktop: isDesktop, children: [_jsxs(ImageNavContainer, { children: [isDesktop && hasMultipleImages && (_jsx(NavButtonContainer, { as: SccPressable, onPress: handlePrev, disabled: !canGoPrev, style: { opacity: !canGoPrev ? 0 : 1 }, elementName: "bbucle-road-modal-nav-prev", logParams: { currentIndex, totalImages: allImages.length }, children: _jsx(IcLeft, { width: 11, height: 21, viewBox: '0 0 11 21', color: color.gray80 }) })), _jsx(ImageContainer, { isDesktop: isDesktop, children: _jsx(ImageScrollView, { ref: scrollViewRef, horizontal: true, pagingEnabled: true, showsHorizontalScrollIndicator: false, onScroll: handleScroll, onMomentumScrollEnd: handleMomentumScrollEnd, scrollEventThrottle: 16, contentOffset: { x: initialIndex * (isDesktop ? 600 : window.innerWidth - 48), y: 0 }, children: allImages.map((image, index) => (_jsx(ImageWrapper, { isDesktop: isDesktop, children: _jsx(SccRemoteImage, { imageUrl: image.url, resizeMode: "contain", style: { borderRadius: 0 }, wrapperBackgroundColor: null }) }, `${image.regionId}-${index}`))) }) }), isDesktop && hasMultipleImages && (_jsx(NavButtonContainer, { as: SccPressable, onPress: handleNext, disabled: !canGoNext, style: { opacity: !canGoNext ? 0 : 1 }, elementName: "bbucle-road-modal-nav-next", logParams: { currentIndex, totalImages: allImages.length }, children: _jsx(IcRight, { width: 11, height: 21, viewBox: '0 0 11 21', color: color.gray80 }) }))] }), hasMultipleImages && (_jsx(PageIndicatorContainer, { children: allImages.map((_, index) => (_jsx(PageDotButton, { as: SccPressable, onPress: () => handleDotPress(index), elementName: "bbucle-road-modal-page-dot", logParams: { targetIndex: index, currentIndex, totalImages: allImages.length }, children: _jsx(PageDot, { active: index === currentIndex }) }, index))) }))] }) }) }) }));
}
const ModalOverlay = styled(TouchableOpacity).attrs({
    activeOpacity: 1,
}) `
  flex: 1;
  background-color: rgba(0, 0, 0, 0.8);
  justify-content: center;
  align-items: center;
`;
const ContentWrapper = styled(TouchableOpacity).attrs({
    activeOpacity: 1,
}) ``;
const ModalContent = styled(View) `
  align-items: center;
  gap: 20px;
  max-width: ${({ isDesktop }) => (isDesktop ? '736px' : 'calc(100vw - 32px)')};
`;
const ImageNavContainer = styled(View) `
  flex-direction: row;
  align-items: center;
  gap: 20px;
`;
const NavButtonContainer = styled(Pressable) `
  width: 48px;
  height: 48px;
  border-radius: 24px;
  background-color: ${color.white};
  justify-content: center;
  align-items: center;
  shadow-color: #000;
  shadow-offset: 0px 0px;
  shadow-opacity: 0.25;
  shadow-radius: 4px;
  elevation: 4;
`;
const ImageContainer = styled(View) `
  width: ${({ isDesktop }) => (isDesktop ? '600px' : 'calc(100vw - 48px)')};
  overflow: hidden;
  border-radius: 0 0 8px 8px;
`;
const ImageScrollView = styled(ScrollView) `
  overflow: hidden;
`;
const ImageWrapper = styled(View) `
  width: ${({ isDesktop }) => (isDesktop ? '600px' : 'calc(100vw - 48px)')};
  justify-content: center;
  align-items: center;
`;
const PageIndicatorContainer = styled(View) `
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;
const PageDotButton = styled(TouchableOpacity) `
  padding: 4px;
`;
const PageDot = styled(View) `
  width: 8px;
  height: 8px;
  border-radius: 4px;
  background-color: ${({ active }) => (active ? color.white : color.gray60)};
`;
